import { useToggle } from "@react-hookz/web";
import first from "lodash/first";
import { type FunctionComponent, useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { SearchResultListToggle } from "scmp-app/components/search/search-result-panel/search-result-list/search-result-list-toggle";
import { checkIfNewsSectionInFirstIndex } from "scmp-app/lib/section";
import type { sectionSearchResultListSectionSearchEdge$key } from "scmp-app/queries/__generated__/sectionSearchResultListSectionSearchEdge.graphql";

import { Container, StyledSectionSearchResultListItem, Title } from "./styles";

export type Props = {
  className?: string;
  reference: sectionSearchResultListSectionSearchEdge$key;
};

export const SectionSearchResultList: FunctionComponent<Props> = ({ className, reference }) => {
  const sections = useFragment(
    graphql`
      fragment sectionSearchResultListSectionSearchEdge on SectionSearchEdge @relay(plural: true) {
        node {
          content {
            entityId
            fullSectionPath {
              ...helpersCheckIfNewsSectionInFirstIndexSection
            }
            ...itemSectionSearchResultListItemSection
          }
        }
      }
    `,
    reference,
  );

  const [isExpand, toggleIsExpand] = useToggle();

  // Check if full section path more than one items or is not news section
  const sanitizedSections = useMemo(
    () =>
      sections?.filter(
        edge =>
          notEmpty(edge.node.content) &&
          edge.node.content?.fullSectionPath?.length &&
          (edge.node.content?.fullSectionPath?.length > 1 ||
            !checkIfNewsSectionInFirstIndex(edge?.node?.content?.fullSectionPath?.[0], 0)),
      ),
    [sections],
  );

  const renderItems = useCallback(() => {
    if (isExpand) {
      return sanitizedSections?.map(
        ({ node }) =>
          notEmpty(node.content) && (
            <StyledSectionSearchResultListItem
              key={node.content.entityId}
              reference={node.content}
            />
          ),
      );
    }
    const firstItem = first(sanitizedSections)?.node;
    return (
      notEmpty(firstItem?.content) && (
        <StyledSectionSearchResultListItem reference={firstItem.content} />
      )
    );
  }, [isExpand, sanitizedSections]);

  return (
    notEmpty(sanitizedSections) && (
      <Container className={className}>
        <Title>Section:</Title>

        {renderItems()}

        {sanitizedSections.length > 1 && (
          <SearchResultListToggle
            isExpand={isExpand}
            onClick={() => toggleIsExpand()}
            toggleMessage={{
              after: "Show less",
              before: "Show all sections",
            }}
          />
        )}
      </Container>
    )
  );
};

SectionSearchResultList.displayName = "SectionSearchResultList";
